import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Mic, ArrowUpRight, ScanLine, ShieldCheck, PlayCircle } from "lucide-react";
import DemoChalanModal from "./DemoChalanModal";
import Waveform from "./Waveform";
import Ticket from "./Ticket";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: "0%",
    transition: {
      duration: 1.1,
      ease: [0.22, 1, 0.36, 1],
      delay: 0.3 + i * 0.12,
    },
  }),
};

const transcript = [
  { word: "Chalan,", cls: "text-[#00b3ff] font-bold" },
  { word: "cobra", cls: "text-white/70" },
  { word: "un", cls: "text-white/70" },
  { word: "PAN", cls: "text-white" },
  { word: "BIMBO", cls: "text-white" },
  { word: "GRANDE,", cls: "text-white" },
  { word: "una", cls: "text-white/70" },
  { word: "COCA", cls: "text-[#ccff00]" },
  { word: "COLA", cls: "text-[#ccff00]" },
  { word: "MEDIANA,", cls: "text-[#ccff00]" },
  { word: "un", cls: "text-white/70" },
  { word: "kilo", cls: "text-white/70" },
  { word: "de", cls: "text-white/70" },
  { word: "AZÚCAR,", cls: "text-white" },
  { word: "medio", cls: "text-white/70" },
  { word: "kilo", cls: "text-white/70" },
  { word: "de", cls: "text-white/70" },
  { word: "FRIJOL", cls: "text-white" },
  { word: "y", cls: "text-white/70" },
  { word: "dame", cls: "text-[#ff5a00]" },
  { word: "el", cls: "text-[#ff5a00]" },
  { word: "total.", cls: "text-[#ff5a00]" },
];

export default function Hero() {
  const ref = useRef(null);
  const [showDemo, setShowDemo] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden pt-28 md:pt-32 pb-16"
      data-testid="hero"
    >
      {/* Ambient glows */}
      <div className="glow-orange" style={{ top: "-10%", left: "-15%" }} />
      <div className="glow-lime" style={{ bottom: "-20%", right: "-10%" }} />

      {/* Grid lines background */}
      <div className="absolute inset-0 bg-grid-lines opacity-40 pointer-events-none" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
        {/* Top row: chip + status */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="chip">
            <span className="rec-dot" /> POS por voz · Gemini 3 Pro
          </div>
          <div className="hidden md:flex items-center gap-6 text-xs font-mono text-white/40 tracking-widest">
            <span>MX · ES</span>
            <span>v1.0 · BETA</span>
            <span className="text-[#ccff00]">EN VIVO</span>
          </div>
        </motion.div>

        {/* Kinetic wordmark */}
        <div className="relative">
          <motion.div style={{ y, opacity }} className="relative">
            <div className="mask-line">
              <motion.h1
                variants={lineReveal}
                custom={0}
                initial="hidden"
                animate="show"
                className="font-display text-[18vw] md:text-[19vw] leading-[0.85] tracking-[-0.05em] text-white select-none"
                data-testid="hero-wordmark"
              >
                CHALANN
              </motion.h1>
            </div>

            {/* Overlay: subtitle italic */}
            <div className="absolute -top-4 right-4 md:top-8 md:right-16 max-w-[280px] hidden md:block">
              <p className="font-mono text-[11px] tracking-[0.25em] text-white/40 uppercase mb-2">
                / Chalán · Ayudante · IA
              </p>
              <p className="font-body text-sm text-white/70 leading-relaxed">
                El mero mero. El que sabe dónde está todo, cobra sin errores y nunca se toma el descanso.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Second row — main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10 md:mt-4">
          {/* Left: line-by-line manifesto reveal */}
          <div className="lg:col-span-7">
            <div className="space-y-1 md:space-y-2">
              {["Habla.", "Vende.", "Repite."].map((line, i) => (
                <span key={line} className="mask-line">
                  <motion.span
                    variants={lineReveal}
                    custom={i + 1}
                    initial="hidden"
                    animate="show"
                    className="inline-block font-display text-6xl md:text-7xl lg:text-8xl tracking-tighter"
                    style={{
                      color:
                        i === 0 ? "#f5f5f5" : i === 1 ? "#ff5a00" : "#ccff00",
                    }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.1 }}
              className="mt-8 max-w-xl text-lg text-white/70 leading-relaxed"
            >
              El primer punto de venta que se maneja <em className="text-white not-italic underline decoration-[#ccff00] decoration-2 underline-offset-4">solo hablando</em>. Sin teclados. Sin filas. Sin errores de captura. Tu tienda cobra, factura y controla inventario mientras tú atiendes al cliente.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.3 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <a href="/setup" className="btn-primary" data-testid="hero-cta-primary">
                <Mic size={18} strokeWidth={2.5} />
                Registrar tu negocio · desde $20 USD
                <ArrowUpRight size={16} />
              </a>
              <button
                onClick={() => setShowDemo(true)}
                className="group relative inline-flex items-center gap-2 pl-4 pr-5 py-3 rounded-full border-2 border-[#CCFF00]/70 bg-[#CCFF00]/10 text-[#CCFF00] font-mono text-xs uppercase tracking-widest overflow-hidden hover:bg-[#CCFF00] hover:text-black transition"
                data-testid="hero-cta-demo"
              >
                <span className="absolute inset-0 bg-[#CCFF00]/15 blur-lg animate-pulse pointer-events-none" />
                <PlayCircle size={16} className="relative" />
                <span className="relative">Prueba · Escucha a Chalán</span>
                <span className="relative ml-1 px-1.5 py-0.5 rounded bg-black/40 text-[9px] text-[#CCFF00] tracking-tighter">
                  DEMO
                </span>
              </button>
              <a href="#producto" className="btn-ghost" data-testid="hero-cta-ghost">
                <ScanLine size={16} />
                Ver cómo funciona
              </a>
              <a
                href="/admin/login"
                className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-[#ccff00] border-l border-white/10 pl-3 ml-1 transition-colors"
                data-testid="hero-admin-link"
                title="Acceso equipo CHALANN"
              >
                <ShieldCheck size={12} />
                Acceso Admin
              </a>
            </motion.div>

            {/* Numbers strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.5 }}
              className="mt-12 grid grid-cols-3 gap-4 max-w-lg"
            >
              {[
                { n: "3×", label: "más rápido\nque un POS táctil" },
                { n: "0", label: "teclas por\nventa promedio" },
                { n: "-42%", label: "tiempo detrás\ndel mostrador" },
              ].map((s, i) => (
                <div key={i} className="border-t border-white/10 pt-3">
                  <p className="font-display text-3xl md:text-4xl tracking-tighter">{s.n}</p>
                  <p className="text-[11px] text-white/50 whitespace-pre-line font-mono tracking-wide leading-relaxed mt-1">
                    {s.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: voice panel + ticket */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-5 md:p-6"
              data-testid="hero-voice-panel"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="rec-dot" />
                  <span className="font-mono text-[11px] tracking-widest text-white/70 uppercase">Escuchando · Doña Maru</span>
                </div>
                <span className="font-mono text-[11px] text-white/40">00:07</span>
              </div>

              <Waveform active={true} />

              <div className="mt-5 rounded-xl bg-black/40 border border-white/5 p-4">
                <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-2">
                  Transcripción en vivo
                </p>
                <p
                  className="font-display text-lg md:text-xl leading-snug tracking-tight"
                  lang="es-MX"
                  translate="no"
                  data-testid="hero-transcript"
                >
                  {transcript.map((t, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.2 + i * 0.06, duration: 0.4 }}
                      className={`${t.cls} inline-block mr-1.5 notranslate`}
                    >
                      {t.word}
                    </motion.span>
                  ))}
                </p>
              </div>

              <div className="mt-4 flex items-center justify-between text-[11px] font-mono text-white/50">
                <span>ES-MX · 98% confianza</span>
                <span className="text-[#ccff00]">→ Ticket listo</span>
              </div>
            </motion.div>

            {/* Floating ticket */}
            <div className="absolute -bottom-16 -right-2 md:-right-8 z-20 hidden sm:block">
              <Ticket />
            </div>
          </div>
        </div>

        {/* Bottom mono ticker */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.8 }}
          className="mt-24 md:mt-32 flex items-center justify-between border-t border-white/10 pt-4 text-[11px] font-mono text-white/40 tracking-widest"
        >
          <span>[ SCROLL ] Descubre CHALANN ↓</span>
          <span>Hecho en México · Para tenderos que no paran</span>
        </motion.div>
      </div>
      <DemoChalanModal open={showDemo} onClose={() => setShowDemo(false)} />
    </section>
  );
}
